import type { ReactNode } from "react";
import { Link } from "@mui/material";

import { AGENT_HELP_ADDRESS } from "@consts/index";

import { IssueEndorsementStatus } from "@redux/slices/endorsement";

const MailTo = ({ address }: { address: string }) => (
  <Link color="inherit" href={`mailto:${address}`}>
    {address}
  </Link>
);

const RENEWAL_MESSAGE = (
  <>
    This endorsement cannot be issued while a renewal exists for this policy.
    Please contact <MailTo address={AGENT_HELP_ADDRESS} /> for assistance.
  </>
);

export const issueEndorsementMessages: Partial<
  Record<IssueEndorsementStatus, ReactNode>
> = {
  [IssueEndorsementStatus.ActiveRenewalError]: RENEWAL_MESSAGE,
  [IssueEndorsementStatus.DraftRenewalError]: RENEWAL_MESSAGE,
  [IssueEndorsementStatus.PaymentError]:
    "Payment failure. To update the payment method, visit the Payments tab on Policy Details, then retry.",
  [IssueEndorsementStatus.UnderwritingReferredError]:
    "Underwriting review required. Click the UW Review tab to proceed.",
  [IssueEndorsementStatus.UnderwritingDeclinedError]:
    "Underwriting review declined.",
  [IssueEndorsementStatus.GlobalError]: (
    <>
      Issuance failed. Something went wrong. Please contact{" "}
      <MailTo address={AGENT_HELP_ADDRESS} /> for assistance.
    </>
  ),
};

/**
 * Resolves the alert copy for a block reason. `ConflictingEndorsementError` is
 * surfaced through its own modal, and any reason without dedicated copy falls
 * back to the generic issuance failure so the agent is never left without
 * feedback.
 */
export function getIssueEndorsementMessage(
  reason?: IssueEndorsementStatus
): ReactNode | undefined {
  if (
    !reason ||
    reason === IssueEndorsementStatus.ConflictingEndorsementError
  ) {
    return undefined;
  }

  return (
    issueEndorsementMessages[reason] ??
    issueEndorsementMessages[IssueEndorsementStatus.GlobalError]
  );
}
